import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Shield, Users, CalendarDays, Activity,
  Settings2, CheckCircle, XCircle, Clock, Pause, Play, Eye,
  ExternalLink, Edit, Save, X, Send, Mail, UserPlus, Lock, Unlock, Plus,
  ChevronRight, BarChart3, MapPin, Globe, Phone, User,
  AlertTriangle, Loader2, FileText, Boxes
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  new_request: { label: "Nouvelle demande", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: Clock },
  reviewing: { label: "En cours d'etude", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", icon: Eye },
  approved: { label: "Approuvee", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: CheckCircle },
  activated: { label: "Active", color: "bg-green-500/10 text-green-400 border-green-500/20", icon: CheckCircle },
  rejected: { label: "Refusee", color: "bg-red-500/10 text-red-400 border-red-500/20", icon: XCircle },
  suspended: { label: "Suspendue", color: "bg-orange-500/10 text-orange-400 border-orange-500/20", icon: Pause },
};

const PERMISSION_LABELS = {
  create_events: "Creer des evenements",
  publish: "Publier des evenements",
  public_registrations: "Inscriptions publiques",
  capacity: "Gestion de capacite",
  waitlist: "Liste d'attente",
  participants: "Gestion des participants",
  manual_add: "Ajout manuel participants",
  reminders: "Envoi de rappels",
  qr_codes: "QR Codes",
  scanner: "Scanner QR",
  statistics: "Statistiques",
  live_screen: "Ecran Live",
  live_control: "Regie Live / Diffusion",
  media_management: "Gestion des medias",
  exports: "Exports (CSV/Excel/PDF)",
  public_directory: "Repertoire public",
};

const TABS = [
  { key: "informations", label: "Informations", icon: Building2 },
  { key: "modules", label: "Modules", icon: Boxes },
  { key: "permissions", label: "Permissions", icon: Shield },
  { key: "utilisateurs", label: "Utilisateurs", icon: Users },
  { key: "evenements", label: "Evenements", icon: CalendarDays },
  { key: "activite", label: "Activite", icon: Activity },
];

const ROLE_LABELS = {
  org_admin: "Administrateur Org",
  event_manager: "Gestionnaire",
  receptionist: "Agent d'accueil",
  viewer: "Observateur",
};

export default function AdminOrgDetailPage() {
  const { token, user: currentUser } = useAuth();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("informations");

  // Permissions state
  const [permissions, setPermissions] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  // Users state
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Activity state
  const [auditLogs, setAuditLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Edit info
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Activation
  const [activationStatus, setActivationStatus] = useState(null);
  const [activating, setActivating] = useState(false);
  const [resending, setResending] = useState(false);

  const fetchOrg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/event/admin/organizations/${orgId}`, { headers });
      setOrg(res.data);
      setEditForm({
        org_name: res.data.org_name || "",
        responsible_name: res.data.responsible_name || "",
        phone: res.data.phone || "",
        city: res.data.city || "",
        country: res.data.country || "",
        website: res.data.website || "",
      });
    } catch (e) {
      toast.error("Organisation non trouvee");
      navigate("/app/event-center");
    } finally {
      setLoading(false);
    }
  }, [orgId, token]);

  const fetchActivationStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/event/admin/organizations/${orgId}/activation-status`, { headers });
      setActivationStatus(res.data);
    } catch { setActivationStatus(null); }
  };

  useEffect(() => { fetchOrg(); fetchActivationStatus(); }, [fetchOrg]);

  // Lazy load tab data
  useEffect(() => {
    if (tab === "permissions" && !permissions) {
      setPermLoading(true);
      axios.get(`${API}/api/admin/org-permissions/${orgId}`, { headers })
        .then(r => setPermissions(r.data))
        .catch(() => toast.error("Erreur chargement permissions"))
        .finally(() => setPermLoading(false));
    }
    if (tab === "utilisateurs" && orgUsers.length === 0) {
      setUsersLoading(true);
      axios.get(`${API}/api/admin/org/${orgId}/users`, { headers })
        .then(r => setOrgUsers(r.data))
        .catch(() => toast.error("Erreur chargement utilisateurs"))
        .finally(() => setUsersLoading(false));
    }
    if (tab === "evenements" && events.length === 0) {
      setEventsLoading(true);
      axios.get(`${API}/api/admin/org/${orgId}/events`, { headers })
        .then(r => setEvents(r.data))
        .catch(() => toast.error("Erreur chargement evenements"))
        .finally(() => setEventsLoading(false));
    }
    if (tab === "activite" && auditLogs.length === 0) {
      setActivityLoading(true);
      axios.get(`${API}/api/admin/org/${orgId}/activity`, { headers })
        .then(r => setAuditLogs(r.data))
        .catch(() => toast.error("Erreur chargement activite"))
        .finally(() => setActivityLoading(false));
    }
  }, [tab]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      const res = await axios.post(`${API}/api/event/admin/organizations/${orgId}/activate`, {}, { headers });
      toast.success(res.data.message);
      await fetchOrg();
      await fetchActivationStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur activation");
    } finally { setActivating(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API}/api/event/admin/organizations/${orgId}/status`, { status }, { headers });
      toast.success(`Statut mis a jour: ${STATUS_MAP[status]?.label || status}`);
      await fetchOrg();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/api/event/admin/organizations/${orgId}/resend-activation`, {}, { headers });
      toast.success(res.data.message);
      await fetchActivationStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur renvoi"); }
    finally { setResending(false); }
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/admin/org/${orgId}/info`, editForm, { headers });
      setOrg(res.data);
      setEditing(false);
      toast.success("Informations mises a jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleSavePermissions = async () => {
    if (!permissions) return;
    setPermSaving(true);
    try {
      const res = await axios.put(`${API}/api/admin/org-permissions/${orgId}`, permissions, { headers });
      setPermissions(res.data);
      toast.success("Permissions mises a jour");
    } catch (e) { toast.error("Erreur sauvegarde permissions"); }
    finally { setPermSaving(false); }
  };

  const togglePermission = (key) => {
    if (!permissions) return;
    setPermissions(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  const toggleFullAccess = () => {
    if (!permissions) return;
    const newFull = !permissions.full_access;
    const newPerms = {};
    Object.keys(PERMISSION_LABELS).forEach(k => { newPerms[k] = newFull; });
    setPermissions(prev => ({ ...prev, full_access: newFull, permissions: newPerms }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#0099CC]" />
    </div>
  );

  if (!org) return null;

  const st = STATUS_MAP[org.status] || STATUS_MAP.new_request;
  const StIcon = st.icon;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-org-detail-page">
      {/* Super Admin Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="super-admin-banner">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-amber-400" />
          <span className="text-amber-400 font-bold text-sm">MODE SUPER ADMIN</span>
          <span className="text-amber-200/70 text-sm">— Vous administrez actuellement</span>
          <span className="text-white font-semibold text-sm">{org.org_name}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/app/event-center")}
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          data-testid="exit-super-admin-btn"
        >
          <X className="w-4 h-4 mr-1" /> Quitter le mode Super Admin
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/event-center")} className="text-[#94A3B8] hover:text-white" data-testid="back-to-event-center">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: (org.primary_color || "#0099CC") + "20" }}>
          <Building2 className="w-6 h-6" style={{ color: org.primary_color || "#0099CC" }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white truncate">{org.org_name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${st.color}`}>
              <StIcon className="w-3 h-3" /> {st.label}
            </span>
            <span className="text-xs text-[#64748B]">Cree le {new Date(org.created_at).toLocaleDateString("fr-FR")}</span>
          </div>
        </div>
        {/* Quick actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {org.status === "activated" && (
            <Button size="sm" onClick={() => navigate(`/app/event-center/org/${orgId}/events`)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="access-org-space-btn">
              <ExternalLink className="w-4 h-4 mr-1" /> Acceder a l'espace organisateur
            </Button>
          )}
          {(org.status === "new_request" || org.status === "reviewing") && (
            <Button size="sm" onClick={handleActivate} disabled={activating} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="activate-org-btn">
              <CheckCircle className="w-4 h-4 mr-1" /> {activating ? "Activation..." : "Approuver et activer"}
            </Button>
          )}
          {org.status === "activated" && (
            <Button size="sm" variant="outline" onClick={() => handleStatusChange("suspended")} className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10" data-testid="suspend-org-btn">
              <Pause className="w-4 h-4 mr-1" /> Suspendre
            </Button>
          )}
          {org.status === "suspended" && (
            <Button size="sm" onClick={() => handleStatusChange("activated")} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="reactivate-org-btn">
              <Play className="w-4 h-4 mr-1" /> Reactiver
            </Button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Evenements", value: org.stats?.events || 0, icon: CalendarDays, color: "#0099CC" },
          { label: "Participants", value: org.stats?.participants || 0, icon: Users, color: "#10B981" },
          { label: "Utilisateurs", value: org.user_count || 0, icon: UserPlus, color: "#8B5CF6" },
          { label: "Plan", value: { free_launch: "Lancement Gratuit", free: "Gratuit", starter: "Starter", pro: "Pro", agency: "Agency" }[org.plan] || org.plan || "free_launch", icon: Shield, color: "#F59E0B" },
        ].map((kpi, i) => (
          <div key={i} className="bg-[#0B1120] border border-[#1A2332] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#64748B] uppercase tracking-wider">{kpi.label}</span>
              <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
            </div>
            <div className="text-xl font-bold text-white capitalize">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-[#0B1120] border border-[#1A2332] rounded-xl overflow-hidden">
        <div className="flex border-b border-[#1A2332] overflow-x-auto" data-testid="org-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#0099CC] text-[#0099CC]"
                  : "border-transparent text-[#64748B] hover:text-white"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ===== TAB: INFORMATIONS ===== */}
          {tab === "informations" && (
            <div className="space-y-6" data-testid="tab-informations-content">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Informations de l'organisation</h3>
                {!editing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="border-[#0099CC]/30 text-[#0099CC] hover:bg-[#0099CC]/10" data-testid="edit-org-btn">
                    <Edit className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="border-[#1A2332] text-[#94A3B8]">
                      <X className="w-4 h-4 mr-1" /> Annuler
                    </Button>
                    <Button size="sm" onClick={handleSaveInfo} disabled={saving} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="save-org-btn">
                      <Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "org_name", label: "Nom de l'organisation", icon: Building2 },
                  { key: "responsible_name", label: "Responsable", icon: User },
                  { key: "phone", label: "Telephone", icon: Phone },
                  { key: "city", label: "Ville", icon: MapPin },
                  { key: "country", label: "Pays", icon: Globe },
                  { key: "website", label: "Site web", icon: Globe },
                ].map(field => (
                  <div key={field.key} className="bg-[#060B14] rounded-lg p-4 border border-[#1A2332]">
                    <label className="text-xs text-[#64748B] uppercase tracking-wider flex items-center gap-1.5 mb-2">
                      <field.icon className="w-3 h-3" /> {field.label}
                    </label>
                    {editing ? (
                      <Input
                        value={editForm[field.key] || ""}
                        onChange={e => setEditForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="bg-[#0B1120] border-[#1A2332] text-white h-9"
                        data-testid={`edit-${field.key}`}
                      />
                    ) : (
                      <p className="text-white text-sm">{org[field.key] || "—"}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Email & Contact */}
              <div className="bg-[#060B14] rounded-lg p-4 border border-[#1A2332]">
                <label className="text-xs text-[#64748B] uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Mail className="w-3 h-3" /> Email de connexion
                </label>
                <p className="text-white text-sm">{org.email}</p>
              </div>

              {/* Activation Status */}
              {activationStatus && (
                <div className="bg-[#060B14] rounded-lg p-4 border border-[#1A2332] space-y-3">
                  <h4 className="text-xs text-[#64748B] uppercase tracking-wider flex items-center gap-1.5">
                    <Shield className="w-3 h-3" /> Statut d'activation du compte
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-[#64748B]">Compte cree: </span>
                      {activationStatus.user_exists ? <span className="text-green-400">Oui</span> : <span className="text-red-400">Non</span>}
                    </div>
                    <div>
                      <span className="text-[#64748B]">Active: </span>
                      {activationStatus.user_active && !activationStatus.needs_activation ? <span className="text-green-400">Oui</span> : <span className="text-orange-400">Non</span>}
                    </div>
                    <div>
                      <span className="text-[#64748B]">Invitation: </span>
                      {activationStatus.invitation_sent ? <span className="text-green-400">Envoyee</span> : <span className="text-[#64748B]">Non envoyee</span>}
                    </div>
                    {activationStatus.last_login && (
                      <div className="col-span-2">
                        <span className="text-[#64748B]">Derniere connexion: </span>
                        <span className="text-white">{new Date(activationStatus.last_login).toLocaleString("fr-FR")}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={handleResend} disabled={resending} className="border-[#0099CC]/30 text-[#0099CC] hover:bg-[#0099CC]/10" data-testid="resend-activation-btn">
                      <Send className="w-4 h-4 mr-1" /> {resending ? "Envoi..." : "Renvoyer l'invitation"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Needs / Requirements */}
              <div className="bg-[#060B14] rounded-lg p-4 border border-[#1A2332]">
                <h4 className="text-xs text-[#64748B] uppercase tracking-wider mb-3">Besoins declares</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-[#64748B]">Type: </span><span className="text-white capitalize">{org.event_type || "—"}</span></div>
                  <div><span className="text-[#64748B]">Participants: </span><span className="text-white">{org.estimated_participants || "—"}</span></div>
                  <div><span className="text-[#64748B]">Date: </span><span className="text-white">{org.planned_date || "—"}</span></div>
                  <div><span className="text-[#64748B]">Frequence: </span><span className="text-white">{org.event_frequency || "—"}</span></div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {org.need_qrcode && <Badge variant="outline" className="text-[#0099CC] border-[#0099CC]/30">QR Code</Badge>}
                  {org.need_access_control && <Badge variant="outline" className="text-[#10B981] border-[#10B981]/30">Controle d'acces</Badge>}
                  {org.need_badges && <Badge variant="outline" className="text-[#8B5CF6] border-[#8B5CF6]/30">Badges</Badge>}
                  {org.need_audiovisual && <Badge variant="outline" className="text-[#F59E0B] border-[#F59E0B]/30">Audiovisuel</Badge>}
                  {org.need_photo_video && <Badge variant="outline" className="text-[#EF4444] border-[#EF4444]/30">Photo/Video</Badge>}
                </div>
              </div>
            </div>
          )}

          {/* ===== TAB: MODULES ===== */}
          {tab === "modules" && (
            <ModulesTab org={org} orgId={orgId} headers={headers} onUpdate={fetchOrg} />
          )}

          {/* ===== TAB: PERMISSIONS ===== */}
          {tab === "permissions" && (
            <div className="space-y-6" data-testid="tab-permissions-content">
              {permLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0099CC]" /></div>
              ) : permissions ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Permissions de l'organisation</h3>
                      <p className="text-xs text-[#64748B] mt-1">Plan actuel: <span className="text-[#F59E0B] font-medium capitalize">{permissions.plan}</span></p>
                    </div>
                    <Button size="sm" onClick={handleSavePermissions} disabled={permSaving} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="save-permissions-btn">
                      <Save className="w-4 h-4 mr-1" /> {permSaving ? "Sauvegarde..." : "Enregistrer les permissions"}
                    </Button>
                  </div>

                  {/* Full Access Toggle */}
                  <div className="bg-[#060B14] rounded-lg p-4 border border-amber-500/20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {permissions.full_access ? <Unlock className="w-5 h-5 text-green-400" /> : <Lock className="w-5 h-5 text-red-400" />}
                      <div>
                        <p className="text-white font-medium">Acces complet Event Center</p>
                        <p className="text-xs text-[#64748B]">Offre de lancement gratuite — toutes les fonctionnalites activees</p>
                      </div>
                    </div>
                    <Switch
                      checked={permissions.full_access}
                      onCheckedChange={toggleFullAccess}
                      data-testid="toggle-full-access"
                    />
                  </div>

                  {/* Individual Permissions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <div key={key} className="bg-[#060B14] rounded-lg px-4 py-3 border border-[#1A2332] flex items-center justify-between">
                        <span className="text-sm text-white">{label}</span>
                        <Switch
                          checked={permissions.permissions?.[key] ?? true}
                          onCheckedChange={() => togglePermission(key)}
                          data-testid={`perm-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-[#64748B]">Erreur chargement permissions</p>
              )}
            </div>
          )}

          {/* ===== TAB: UTILISATEURS ===== */}
          {tab === "utilisateurs" && (
            <div className="space-y-4" data-testid="tab-utilisateurs-content">
              <h3 className="text-lg font-semibold text-white">Utilisateurs ({orgUsers.length})</h3>
              {usersLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0099CC]" /></div>
              ) : orgUsers.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Aucun utilisateur dans cette organisation</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1A2332]">
                  {orgUsers.map(u => (
                    <div key={u.id} className="py-3 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#0099CC]/20 flex items-center justify-center">
                          <User className="w-4 h-4 text-[#0099CC]" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{u.full_name || u.email}</p>
                          <p className="text-xs text-[#64748B]">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-[#F59E0B] border-[#F59E0B]/30 text-xs">
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                        {u.needs_activation ? (
                          <span className="text-xs text-orange-400">En attente d'activation</span>
                        ) : (
                          <span className="text-xs text-green-400">Actif</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== TAB: EVENEMENTS ===== */}
          {tab === "evenements" && (
            <div className="space-y-4" data-testid="tab-evenements-content">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Evenements ({events.length})</h3>
                <div className="flex gap-2">
                  <AdminCreateEventButton orgId={orgId} headers={headers} onCreated={fetchEvents} />
                  {org.status === "activated" && (
                    <Button size="sm" onClick={() => navigate(`/app/event-center/org/${orgId}/events`)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="view-all-events-btn">
                      <ExternalLink className="w-4 h-4 mr-1" /> Ouvrir l'espace
                    </Button>
                  )}
                </div>
              </div>
              {eventsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0099CC]" /></div>
              ) : events.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Aucun evenement cree</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1A2332]">
                  {events.map(ev => (
                    <div
                      key={ev.id}
                      className="py-3 flex items-center justify-between hover:bg-[#0D1424] transition-colors cursor-pointer px-2 rounded-lg -mx-2"
                      onClick={() => navigate(`/app/event-center/org/${orgId}/event/${ev.id}`)}
                      data-testid={`event-row-${ev.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          ev.status === "published" ? "bg-green-400" : ev.status === "ended" ? "bg-[#8B5CF6]" : ev.status === "cancelled" ? "bg-red-400" : "bg-[#64748B]"
                        }`} />
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{ev.title}</p>
                          <p className="text-xs text-[#64748B]">
                            {ev.event_date ? new Date(ev.event_date).toLocaleDateString("fr-FR") : "Date non definie"}
                            {ev.location ? ` — ${ev.location}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-[#0099CC]">{ev.total_registered || 0} inscrits</p>
                          <p className="text-xs text-[#64748B]">{ev.total_checked_in || 0} presents</p>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          ev.status === "published" ? "text-green-400 border-green-400/30" :
                          ev.status === "ended" ? "text-[#8B5CF6] border-[#8B5CF6]/30" :
                          ev.status === "cancelled" ? "text-red-400 border-red-400/30" :
                          "text-[#64748B] border-[#64748B]/30"
                        }`}>
                          {ev.status === "published" ? "Publie" : ev.status === "ended" ? "Termine" : ev.status === "cancelled" ? "Annule" : ev.status === "draft" ? "Brouillon" : ev.status}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-[#64748B]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== TAB: ACTIVITE ===== */}
          {tab === "activite" && (
            <div className="space-y-4" data-testid="tab-activite-content">
              <h3 className="text-lg font-semibold text-white">Journal d'activite</h3>
              {activityLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0099CC]" /></div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Aucune activite enregistree</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map((log, i) => (
                    <div key={log.id || i} className="bg-[#060B14] rounded-lg px-4 py-3 border border-[#1A2332] flex items-start gap-3" data-testid={`audit-log-${i}`}>
                      <div className="w-8 h-8 rounded-full bg-[#0099CC]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Activity className="w-4 h-4 text-[#0099CC]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{formatAction(log.action)}</p>
                        <p className="text-xs text-[#64748B] mt-0.5">
                          {log.admin_email} — {new Date(log.created_at).toLocaleString("fr-FR")}
                        </p>
                        {log.details && (
                          <p className="text-xs text-[#94A3B8] mt-1">
                            {typeof log.details === "object" ? JSON.stringify(log.details) : log.details}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatAction(action) {
  const map = {
    view_org_events: "Consultation des evenements",
    update_org_permissions: "Modification des permissions",
    update_org_info: "Modification des informations",
    update_org_modules: "Modification des modules",
    vip_capacity_override: "Ajout VIP (depassement capacite)",
    cancel_participant: "Annulation d'un participant",
    manual_add_participant: "Ajout manuel d'un participant",
  };
  return map[action] || action.replace(/_/g, " ");
}

const MODULE_CONFIG = [
  {
    key: "event_center",
    label: "GLOTECH Event Center",
    description: "Gestion evenementielle complete : inscriptions, QR, check-in, statistiques, live control.",
    color: "#F59E0B",
    icon: CalendarDays,
  },
  {
    key: "web",
    label: "GLOTECH Web",
    description: "CRM, gestion de projets, facturation, propositions commerciales, SEO.",
    color: "#0099CC",
    icon: Globe,
  },
  {
    key: "projects",
    label: "GLOTECH Projects",
    description: "Gestion de projets clients, suivi d'avancement, livrables. (Bientot disponible)",
    color: "#10B981",
    icon: Building2,
    comingSoon: true,
  },
];

function ModulesTab({ org, orgId, headers, onUpdate }) {
  const [modules, setModules] = useState(org?.active_modules || []);
  const [saving, setSaving] = useState(false);

  const toggleModule = (key) => {
    setModules(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/event/admin/organizations/${orgId}/modules`, { active_modules: modules }, { headers });
      toast.success("Modules mis a jour avec succes");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(modules.sort()) !== JSON.stringify((org?.active_modules || []).sort());

  return (
    <div className="space-y-6" data-testid="tab-modules-content">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Modules actifs</h3>
          <p className="text-xs text-[#64748B] mt-1">Activez ou desactivez les espaces de travail pour cette organisation</p>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`${hasChanges ? "bg-[#10B981] hover:bg-[#0d9668]" : "bg-[#1A2332]"} text-white`}
          data-testid="save-modules-btn"
        >
          <Save className="w-4 h-4 mr-1" /> {saving ? "Sauvegarde..." : "Enregistrer"}
        </Button>
      </div>

      <div className="space-y-3">
        {MODULE_CONFIG.map(mod => {
          const isActive = modules.includes(mod.key);
          const ModIcon = mod.icon;
          return (
            <div
              key={mod.key}
              className={`bg-[#060B14] rounded-xl p-5 border-2 transition-all ${
                isActive ? "border-opacity-50" : "border-[#1A2332] opacity-60"
              } ${mod.comingSoon ? "opacity-40 pointer-events-none" : ""}`}
              style={{ borderColor: isActive ? mod.color : undefined }}
              data-testid={`module-card-${mod.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: mod.color + "15" }}>
                    <ModIcon className="w-6 h-6" style={{ color: mod.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">{mod.label}</h4>
                      {mod.comingSoon && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#64748B]/20 text-[#64748B] font-medium">BIENTOT</span>
                      )}
                    </div>
                    <p className="text-xs text-[#64748B] mt-1 max-w-md">{mod.description}</p>
                  </div>
                </div>
                {!mod.comingSoon && (
                  <Switch
                    checked={isActive}
                    onCheckedChange={() => toggleModule(mod.key)}
                    data-testid={`toggle-module-${mod.key}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modules.length === 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
          <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-400">Attention : aucun module actif. L'organisation ne pourra acceder a aucun espace de travail.</p>
        </div>
      )}
    </div>
  );
}


function AdminCreateEventButton({ orgId, headers, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", location: "", event_date: "", capacity: 100 });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/organizations/${orgId}/events`, form, { headers });
      toast.success("Evenement cree avec succes");
      setForm({ title: "", location: "", event_date: "", capacity: 100 });
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur creation");
    } finally { setCreating(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10" data-testid="admin-create-event-btn">
        <CalendarDays className="w-4 h-4 mr-1" /> Creer pour le client
      </Button>
    );
  }

  return (
    <div className="bg-[#060B14] border border-[#F59E0B]/20 rounded-xl p-4 space-y-3 w-full" data-testid="admin-create-event-form">
      <h4 className="text-sm font-semibold text-[#F59E0B]">Creer un evenement pour cette organisation</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Titre de l'evenement *" className="bg-[#0B0F19] border-[#1E293B] text-white" data-testid="admin-event-title" />
        <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Lieu" className="bg-[#0B0F19] border-[#1E293B] text-white" />
        <Input type="datetime-local" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))} className="bg-[#0B0F19] border-[#1E293B] text-white" />
        <Input type="number" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: parseInt(e.target.value) || 100 }))} placeholder="Capacite" className="bg-[#0B0F19] border-[#1E293B] text-white" />
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="text-[#64748b]">Annuler</Button>
        <Button size="sm" onClick={handleCreate} disabled={creating} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="submit-admin-event">
          {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-1" />} Creer
        </Button>
      </div>
    </div>
  );
}
